"use client";
import { IBaseLabelValue } from "@/interfaces";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import ResultContent from "../../resultContent";
import { useRef, useState } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedPassportStatusInquiryResData } from "@/interfaces/inquiries/governmental";

const PassportStatusInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedPassportStatusInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const passportStatusData = data.data.data.epass;
  const ziaratiPassportStatusData = data.data.data.ziarati;

  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);

  const passportStatusInput = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("passportStatus.nationalCode"),
      value: passportStatusInput.national_code,
    },
  ];

  const resultContent = [
    {
      label: t("passportStatus.issueDate"),
      value: passportStatusData.tarikhSodoor || "نامشخص",
    },
    {
      label: t("passportStatus.validityDate"),
      value: passportStatusData.tarikhEtebar || "نامشخص",
    },
    {
      label: t("passportStatus.validityStatus"),
      value: passportStatusData.vaziatPassport || "نامشخص",
    },
    {
      label: t("passportStatus.passNumber"),
      value: passportStatusData.shomarehPassport || "نامشخص",
    },
    {
      label: t("passportStatus.request"),
      value: passportStatusData.hasDarkhast
        ? t("passportStatus.hasRequest")
        : t("passportStatus.noRequest"),
    },
    {
      label: t("passportStatus.personStatus"),
      value: passportStatusData.shakhsFinded
        ? t("passportStatus.foundPerson")
        : t("passportStatus.notFoundPerson"),
    },
    {
      label: t("passportStatus.passportStatus"),
      value: passportStatusData.hasPassport
        ? t("passportStatus.hasPassport")
        : t("passportStatus.noPassport"),
    },
    ...(passportStatusData.vaziatDarkhast
      ? [
          {
            label: t("passportStatus.requestStatus"),
            value: passportStatusData.vaziatDarkhast,
          },
        ]
      : []),
    ...(passportStatusData.tarikhDarkhast
      ? [
          {
            label: t("passportStatus.requestDate"),
            value: passportStatusData.tarikhDarkhast,
          },
        ]
      : []),
    ...(passportStatusData.barcodePosti
      ? [
          {
            label: t("passportStatus.postBarcode"),
            value: passportStatusData.barcodePosti,
          },
        ]
      : []),
  ];

  const ziaratiResultContent = [
    {
      label: t("passportStatus.issueDate"),
      value: ziaratiPassportStatusData.tarikhSodoor || "نامشخص",
    },
    {
      label: t("passportStatus.validityDate"),
      value: ziaratiPassportStatusData.tarikhEtebar || "نامشخص",
    },
    {
      label: t("passportStatus.validityStatus"),
      value: ziaratiPassportStatusData.vaziatPassport || "نامشخص",
    },
    {
      label: t("passportStatus.passNumber"),
      value: ziaratiPassportStatusData.shomarehPassport || "نامشخص",
    },
    {
      label: t("passportStatus.request"),
      value: ziaratiPassportStatusData.hasDarkhast
        ? t("passportStatus.hasRequest")
        : "هیچ درخواستی برای گذرنامه زیارتی ثبت نشده است",
    },
    {
      label: t("passportStatus.personStatus"),
      value: ziaratiPassportStatusData.shakhsFinded
        ? t("passportStatus.foundPerson")
        : t("passportStatus.notFoundPerson"),
    },
    {
      label: t("passportStatus.passportStatus"),
      value: ziaratiPassportStatusData.hasPassport
        ? t("passportStatus.hasPassport")
        : t("passportStatus.noPassport"),
    },
    ...(ziaratiPassportStatusData.vaziatDarkhast
      ? [
          {
            label: t("passportStatus.requestStatus"),
            value: ziaratiPassportStatusData.vaziatDarkhast,
          },
        ]
      : []),
    ...(ziaratiPassportStatusData.tarikhDarkhast
      ? [
          {
            label: t("passportStatus.requestDate"),
            value: ziaratiPassportStatusData.tarikhDarkhast,
          },
        ]
      : []),
    ...(ziaratiPassportStatusData.barcodePosti
      ? [
          {
            label: t("passportStatus.postBarcode"),
            value: ziaratiPassportStatusData.barcodePosti,
          },
        ]
      : []),
  ];

  const passportAccordions = [
    {
      key: "epas",
      label: "گذرنامه عادی",
      data: resultContent,
    },
    {
      key: "ziarati",
      label: "گذرنامه زیارتی",
      data: ziaratiResultContent,
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("passportStatus.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="passport-status-inquiry-download-pdf"
            fileName={`استعلام وضعیت گذرنامه ${passportStatusInput.national_code}`}
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
          />
        </FlexBetween>
        <div className="mb-5">
          {passportAccordions.map((item, i) => (
            <div className="mb-3">
              <Accordion
                key={item.key}
                title={item.label}
                open={item.key === openAccordionId}
                hasIcon={false}
                onOpenChange={(isOpen) => {
                  setOpenAccordionId(isOpen ? item.key : null);
                }}
              >
                <ResultContent content={item.data} />
              </Accordion>
            </div>
          ))}
        </div>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="passport-status-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`استعلام وضعیت گذرنامه ${passportStatusInput.national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-passport-status-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-passport-status-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Passport_Status.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle={`استعلام وضعیت گذرنامه ${passportStatusInput.national_code} - گذرنامه عادی`}
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
        <PdfTemplate
          tableTitle={`استعلام وضعیت گذرنامه ${passportStatusInput.national_code} - گذرنامه زیارتی`}
          resultContent={ziaratiResultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default PassportStatusInquiryDetails;
